import React, { useCallback, useEffect, useState } from "react";
import { FileText, Globe } from "lucide-react";
import { toast } from "sonner";
import { ccInput, ccLabel, ccCard } from "./care/ui";

const API = process.env.REACT_APP_BACKEND_URL;

const CONTENT_LABELS = {
  hero_badge: "Landing — hero badge pill",
  hero_heading: "Landing — hero headline",
  hero_subheading: "Landing — hero subheading",
  showcase_heading: "Landing — showcase strip heading",
  showcase_empty_title: "Landing — showcase empty-state title",
  showcase_empty_text: "Landing — showcase empty-state text",
  footer_tagline: "Landing — footer tagline",
};
const META_PAGES = ["landing", "about", "contact", "store", "providers", "terms", "privacy", "care", "dashboard", "master"];
const LIVE_PREVIEW_PAGES = [["landing", "Landing"], ["store", "Store"], ["care", "Care Connections"], ["providers", "Provider Directory"], ["banner", "Global Banner (all pages)"]];

export default function SiteContentEditor() {
  const [values, setValues] = useState(null);
  const [saving, setSaving] = useState(null);
  const [livePages, setLivePages] = useState(null);

  const load = useCallback(() => {
    fetch(`${API}/api/site-content`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d) setValues({ ...d.content, ...d.meta });
      })
      .catch(() => {});
    fetch(`${API}/api/site/status`)
      .then((r) => (r.ok ? r.json() : null))
      .then((s) => {
        if (s) setLivePages(s.live_preview_pages || []);
      })
      .catch(() => {});
  }, []);
  useEffect(() => load(), [load]);

  const save = async (key) => {
    setSaving(key);
    try {
      const res = await fetch(`${API}/api/master/site-content`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ updates: { [key]: values[key] ?? "" } }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Save failed");
      window.dispatchEvent(new Event("nt-content-changed"));
      toast.success("Published — live across the site.");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(null);
    }
  };

  const toggleLivePage = async (key) => {
    const prev = livePages;
    const next = livePages.includes(key) ? livePages.filter((p) => p !== key) : [...livePages, key];
    setLivePages(next);
    try {
      const res = await fetch(`${API}/api/master/live-preview-pages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ pages: next }),
      });
      if (!res.ok) throw new Error();
      toast.success("Live preview pages updated — applies instantly across the site.");
    } catch {
      setLivePages(prev);
      toast.error("Could not update live preview pages.");
    }
  };

  const row = (key, label, multiline, placeholder) => (
    <div key={key} className="p-3.5 bg-[#FFF9F2] border border-[#FCE6E9] rounded-2xl">
      <label className={ccLabel}>{label}</label>
      {multiline ? (
        <textarea rows={2} value={values[key] ?? ""} placeholder={placeholder} onChange={(e) => setValues({ ...values, [key]: e.target.value })} className={ccInput} data-testid={`content-edit-${key}`} />
      ) : (
        <input value={values[key] ?? ""} placeholder={placeholder} onChange={(e) => setValues({ ...values, [key]: e.target.value })} className={ccInput} data-testid={`content-edit-${key}`} />
      )}
      <div className="flex justify-end mt-2">
        <button onClick={() => save(key)} disabled={saving === key} data-testid={`content-save-${key}`} className="px-4 py-1.5 bg-[#F8C8DC] hover:bg-[#EAAFC5] text-[#374151] rounded-full text-[10px] font-extrabold transition-colors disabled:opacity-50">
          {saving === key ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  );

  if (!values) return null;

  return (
    <div className={`${ccCard} p-6`} data-testid="site-content-panel">
      <h3 className="text-base font-extrabold font-heading flex items-center gap-2.5 mb-1">
        <span className="w-9 h-9 rounded-full bg-[#E6F2FA] flex items-center justify-center">
          <FileText className="w-4 h-4 text-[#5CB3DC]" />
        </span>
        Site Content
      </h3>
      <p className="text-[11px] text-[#9CA3AF] font-medium mb-4">
        Manually edit written copy across the site. Saves publish instantly — no redeploy needed. Masters can also edit hero/showcase text inline on the landing page (pencil icons).
      </p>

      <h4 className="text-sm font-extrabold font-heading flex items-center gap-2 mb-3">
        <Globe className="w-4 h-4 text-rose-400" /> Live Preview Pages
      </h4>
      <p className="text-[11px] text-[#9CA3AF] font-medium mb-3">Choose where live strips and the global upcoming-live banner appear. Cut a location off and it disappears there instantly.</p>
      <div className="flex flex-wrap gap-2 mb-6" data-testid="live-preview-pages">
        {livePages === null ? (
          <p className="text-[11px] text-[#9CA3AF] font-semibold">Loading…</p>
        ) : (
          LIVE_PREVIEW_PAGES.map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => toggleLivePage(key)}
              data-testid={`live-page-${key}`}
              className={`px-3.5 py-2 rounded-full text-[11px] font-extrabold border transition-colors ${
                livePages.includes(key) ? "bg-[#E2F4E1] border-[#BEE3BD] text-[#2F6031]" : "bg-gray-50 border-gray-200 text-gray-400"
              }`}
            >
              {label} {livePages.includes(key) ? "On" : "Off"}
            </button>
          ))
        )}
      </div>
      <div className="space-y-3">{Object.entries(CONTENT_LABELS).map(([key, label]) => row(key, label, key.includes("subheading") || key.includes("text")))}</div>

      <h4 className="text-sm font-extrabold font-heading flex items-center gap-2 mt-6 mb-3">
        <Globe className="w-4 h-4 text-[#5CB3DC]" /> Per-Page SEO Meta
      </h4>
      <p className="text-[11px] text-[#9CA3AF] font-medium mb-4">Override each page's browser title and search/social description. Leave blank to keep the built-in default.</p>
      <div className="space-y-3">
        {META_PAGES.map((page) => (
          <div key={page} className="p-3.5 bg-[#FFF9F2] border border-[#FCE6E9] rounded-2xl" data-testid={`meta-page-${page}`}>
            <p className="text-[11px] font-extrabold uppercase tracking-wider text-[#9CA3AF] mb-2">/{page === "landing" ? "" : page}</p>
            <div className="grid grid-cols-1 gap-2.5">
              <div>
                <label className={ccLabel}>Meta title</label>
                <input value={values[`meta_${page}_title`] ?? ""} placeholder="Default title" onChange={(e) => setValues({ ...values, [`meta_${page}_title`]: e.target.value })} className={ccInput} data-testid={`meta-edit-${page}-title`} />
              </div>
              <div>
                <label className={ccLabel}>Meta description</label>
                <textarea rows={2} value={values[`meta_${page}_description`] ?? ""} placeholder="Default description" onChange={(e) => setValues({ ...values, [`meta_${page}_description`]: e.target.value })} className={ccInput} data-testid={`meta-edit-${page}-description`} />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-2">
              <button onClick={() => save(`meta_${page}_title`)} disabled={saving === `meta_${page}_title`} data-testid={`meta-save-${page}-title`} className="px-4 py-1.5 bg-[#E6F2FA] hover:bg-[#B3E0F2] text-[#3D7E9E] rounded-full text-[10px] font-extrabold transition-colors disabled:opacity-50">
                Save Title
              </button>
              <button onClick={() => save(`meta_${page}_description`)} disabled={saving === `meta_${page}_description`} data-testid={`meta-save-${page}-description`} className="px-4 py-1.5 bg-[#E6F2FA] hover:bg-[#B3E0F2] text-[#3D7E9E] rounded-full text-[10px] font-extrabold transition-colors disabled:opacity-50">
                Save Description
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
